import {
    PocDivider,
    PocList,
    PocListItem,
    PocProgressSpinner,
    PocTypography,
} from 'commons/components/components-wrapper';
import { Size } from '@one/web-components';
import { OwcListItemCustomEvent } from '@one/web-components/dist/types/components';
import { HighlightedText } from 'commons/components/highlighted-text';
import { useVirtualization } from 'commons/hooks';
import { useGroupExpansion } from '../hooks/use-group-expansion.hook';
import { useTranslation } from 'core/i18n/i18n';
import React, { useEffect } from 'react';
import {
    getFilteredFlatOptions,
    getFilteredNestedChildren,
    getFilteredNestedOptions,
    getFilteredNestedOptionsWithStructure,
    getSelectedhiddenOptions,
} from '../searchable-dropdown.business';
import { LoadingContainer, OptionLabel, OptionMarker, SearchableOwcList } from '../searchable-dropdown.style';
import { CustomFilterSearch, NestedSelectableOptions, SelectableOptions } from '../searchable-dropdown.type';
import { SearchableDropdownOptionText } from './searchable-dropdown-option-text.component';

interface SearchableDropdownListProps {
    isNestedList: boolean;
    selectedValues?: string[];
    options: SelectableOptions[] | NestedSelectableOptions[];
    defaultFilter: string;
    fixedOptions?: SelectableOptions[];
    isLoading?: boolean;
    size?: Exclude<Size, 'xSmall' | 'large' | 'xLarge'>;
    customFilter?: CustomFilterSearch;
    includeFakeOption?: boolean;
    visible?: boolean;
    virtualized?: boolean;
    expandGroupsByDefault?: boolean;
    disableEmptyGroups?: boolean;
}

export const SearchableDropdownList = ({
    isNestedList,
    selectedValues,
    options,
    defaultFilter,
    fixedOptions = [],
    isLoading,
    size,
    customFilter,
    includeFakeOption,
    visible = false,
    virtualized,
    expandGroupsByDefault = false,
    disableEmptyGroups = false,
}: SearchableDropdownListProps) => {
    const scrollContainerRef = React.useRef<HTMLOwcListElement | null>(null);
    const { expandedItems, toggleExpanded } = useGroupExpansion({
        groups: isNestedList ? (options as NestedSelectableOptions[]) : [],
        visible,
        expandGroupsByDefault,
    });
    const { t } = useTranslation();

    const filteredOptions = isNestedList
        ? getFilteredNestedOptionsWithStructure(options as NestedSelectableOptions[], defaultFilter)
        : getFilteredFlatOptions(options, defaultFilter);

    const filteredFixedOptions = isNestedList
        ? getFilteredNestedOptionsWithStructure(fixedOptions as NestedSelectableOptions[], defaultFilter)
        : getFilteredFlatOptions(fixedOptions, defaultFilter);

    const selectableNestedOptions = disableEmptyGroups ? getFilteredNestedChildren : getFilteredNestedOptions;
    const flatOptions = isNestedList ? selectableNestedOptions(options as NestedSelectableOptions[], '') : options;

    const { shouldVirtualize, virtualPaddingTop, virtualPaddingBottom, measureElementRef, virtualRows } =
        useVirtualization<SelectableOptions>({
            disabled: !virtualized || !visible,
            overscan: 5,
            scrollContainerRef,
            estimateRowHeight: 48,
            data: filteredOptions,
        });

    const getItemsToRender = () => {
        if (virtualized && !visible) {
            return [];
        }

        return shouldVirtualize
            ? virtualRows.map((vRow) => ({ index: vRow.index, data: filteredOptions[vRow.index] }))
            : filteredOptions.map((data, index) => ({ index, data }));
    };

    const itemsToRender = getItemsToRender();

    const allOptions = [...fixedOptions, ...flatOptions];
    const allVisibleOptions = [...filteredFixedOptions, ...itemsToRender.map((item) => item.data)];
    const selectedHiddenOptions = getSelectedhiddenOptions(allOptions, allVisibleOptions, selectedValues, customFilter);
    const highlightValue = defaultFilter || (customFilter?.filter ?? '');

    const hasPartialOptionsSelected = (() => {
        if (customFilter) {
            const selectedCount = customFilter.selectedOptions?.length ?? 0;
            return selectedCount > 0;
        }

        const selectedCount = selectedValues?.length ?? 0;
        return selectedCount > 0 && selectedCount < allOptions.length;
    })();

    useEffect(() => {
        if (scrollContainerRef.current && (visible || defaultFilter)) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [visible, defaultFilter]);

    return (
        <SearchableOwcList
            data-testid="searchable-dropdown-list"
            size={size}
            ref={scrollContainerRef}
            data-virtualization={shouldVirtualize}
        >
            <div>
                {isLoading ? (
                    <LoadingContainer>
                        <PocProgressSpinner data-testid="searchable-dropdown-loading" variant="primary" size="medium" />
                        <PocTypography element="span" variant="subtitle1">
                            {t('commons.components.searchable-dropdown.results.loading')}
                        </PocTypography>
                    </LoadingContainer>
                ) : (
                    <>
                        {filteredFixedOptions.map((option) => {
                            const nameLabel = <HighlightedText value={highlightValue} text={option.name} />;
                            return (
                                <PocListItem
                                    data-testid="searchable-dropdown-list-item"
                                    noBorder
                                    key={option.id}
                                    value={option.id}
                                >
                                    <SearchableDropdownOptionText label={nameLabel} tooltipText={option.name} />
                                </PocListItem>
                            );
                        })}
                        {filteredFixedOptions?.length > 0 && <PocDivider />}
                        {shouldVirtualize && virtualPaddingTop > 0 ? (
                            <div style={{ height: virtualPaddingTop }} />
                        ) : null}
                        {itemsToRender.map(({ index, data: option }) => {
                            const nameLabel = <HighlightedText value={highlightValue} text={option.name} />;

                            const nestedChildren =
                                isNestedList && (option as NestedSelectableOptions).children
                                    ? (option as NestedSelectableOptions).children
                                    : [];

                            return (
                                <div
                                    key={`list-option-${option.id}`}
                                    data-index={index}
                                    ref={shouldVirtualize ? measureElementRef : undefined}
                                >
                                    <PocListItem
                                        data-testid="searchable-dropdown-list-item"
                                        noBorder
                                        value={option.id}
                                        disabled={
                                            (disableEmptyGroups && isNestedList && !nestedChildren.length) || undefined
                                        }
                                        expanded={expandedItems.has(option.id) || undefined}
                                        onExpandedChange={(e: OwcListItemCustomEvent<boolean>) => {
                                            toggleExpanded(option.id, e.detail === true);
                                        }}
                                    >
                                        <OptionLabel>
                                            <SearchableDropdownOptionText label={nameLabel} tooltipText={option.name} />
                                            {option.marker ? <OptionMarker>{option.marker}</OptionMarker> : null}
                                        </OptionLabel>
                                        {option.secondaryText ? (
                                            <SearchableDropdownOptionText
                                                slot="secondary"
                                                label={
                                                    <HighlightedText
                                                        value={highlightValue}
                                                        text={option?.secondaryText}
                                                    />
                                                }
                                                tooltipText={option.secondaryText}
                                            />
                                        ) : null}

                                        {nestedChildren.length > 0 ? (
                                            <PocList key={`${option.id}-children`} slot="children">
                                                {nestedChildren.map((child) => (
                                                    <PocListItem
                                                        data-testid="searchable-dropdown-list-item"
                                                        noBorder
                                                        value={child.id}
                                                        key={child.id}
                                                    >
                                                        <OptionLabel>
                                                            <SearchableDropdownOptionText
                                                                label={
                                                                    <HighlightedText
                                                                        value={highlightValue}
                                                                        text={child.name}
                                                                    />
                                                                }
                                                                tooltipText={child.name}
                                                            />
                                                            {child.marker ? (
                                                                <OptionMarker>{child.marker}</OptionMarker>
                                                            ) : null}
                                                        </OptionLabel>
                                                    </PocListItem>
                                                ))}
                                            </PocList>
                                        ) : undefined}
                                    </PocListItem>
                                </div>
                            );
                        })}
                        {shouldVirtualize && virtualPaddingBottom > 0 ? (
                            <div style={{ height: virtualPaddingBottom }} />
                        ) : null}
                        {selectedHiddenOptions.map((selectedHiddenOption) => (
                            <PocListItem
                                data-testid="searchable-dropdown-list-item"
                                key={selectedHiddenOption.id}
                                value={selectedHiddenOption.id}
                                className={'hidden'}
                            >
                                {selectedHiddenOption.name}
                                <span slot="secondary">{selectedHiddenOption.secondaryText}</span>
                            </PocListItem>
                        ))}
                        {includeFakeOption || hasPartialOptionsSelected ? (
                            <PocListItem
                                data-testid="searchable-dropdown-list-item"
                                value={'custom-filter-fake-option'}
                                className={'hidden'}
                            ></PocListItem>
                        ) : undefined}
                    </>
                )}
            </div>
        </SearchableOwcList>
    );
};
